import { Component, OnInit, ViewChild } from '@angular/core';
import { CdkStepper } from '@angular/cdk/stepper';
import { OrcamentoService } from '../../../services/orcamento.service';
import { Orcamento } from '../../../models/orcamento.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { ActivatedRoute, Router } from '@angular/router';
import { BudgetService } from '../../../services/budget.service';
import { GruposItensDto, Grupo } from '../../../dtos/grupos-itens.dto';
import { BudgetCategory } from '../../../models/budget-category.model';
import { FormParametroOrcamentoComponent } from '../components/form-parametro-orcamento/form-parametro-orcamento.component';
import { AuthService } from 'src/app/core/services/auth/auth.service';

// Rela responsável pela primeira visão de orçamento.
@Component({
  selector: 'app-budget-view',
  templateUrl: './budget-view.component.html',
  styleUrl: './budget-view.component.scss'
})
export class BudgetViewComponent implements OnInit {
  @ViewChild('cdkStepper') cdkStepper!: CdkStepper;
  @ViewChild('formParametro') formParametroComponent!: FormParametroOrcamentoComponent;

  breadCrumbItems: Array<{}> = [];

  orcamento!: Orcamento | null;
  gruposItens: GruposItensDto[] = [];
  orcamentosRecebidos?: Grupo[] =[];

  loading: boolean = false;

  // Permissões
  podeEditar = false;
  erro403 = false;

  constructor(
    private orcamentoService: OrcamentoService,
    public budgetService: BudgetService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Projeto" }, 
      { label: "Orçamento", active: true },
    ];

    this.verificarPermissoes();
    
    if (!this.podeEditar) {
      this.erro403 = true;
      return;
    }

    const orcamentoId = this.route.snapshot.paramMap.get('id');

    // Se for "novo", carrega parâmetros padrão
    if (orcamentoId && orcamentoId !== 'novo') {
      this.carregarOrcamento(orcamentoId);
      this.carregarListaDeItens(orcamentoId);
    } else {
      // Novo orçamento - carrega parâmetros padrão para preencher o formulário
      this.carregarParametrosPadrao();
      // Carrega as categorias disponíveis para exibir os grupos vazios
      this.carregarCategoriasDisponiveis();
    }
  }

  // preencher o form com os valores que ja existem
  //Somente orcamento é carregado aqui.
  carregarOrcamento(orcamentoId: string): void {

    this.loading = true;

    this.orcamentoService.consultarOrcamento(orcamentoId).subscribe({
      next: (parametro: Orcamento) => {
        // Mapear campos da API para o modelo, caso a API retorne com nomes diferentes
        const orcamentoApi: any = parametro;
        
        this.orcamento = {
          ...parametro,
          descricao: parametro.descricao || orcamentoApi.descricao || '',
          projetoId: parametro.projetoId || orcamentoApi.projetoId || orcamentoApi.projetoFeiraId || '',
          feira: parametro.feira || orcamentoApi.feira || orcamentoApi.nomeFeira || ''
        };
      },
      error: () => {
        this.toastService.show("Erro ao carregar parâmetros", {
          classname: "bg-danger text-light",
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  carregarParametrosPadrao(): void {
    this.loading = true;

    this.orcamentoService.consultarParametrosPadrao().subscribe({
      next: (parametrosPadrao) => {
        // Converte os parâmetros padrão para o formato Orcamento
        // Os campos de dimensões (largura, comprimento, etc.) ficam vazios para preenchimento
        this.orcamento = {
          id: undefined, // Novo orçamento não tem ID ainda
          largura: 0,
          comprimento: 0,
          larguraSobrepor: 0,
          comprimentoSobrepor: 0,
          percentualImpostoFaturaIsentoIss: parametrosPadrao.percentualImpostoFaturaIsentoIss,
          percentualCustoOperacional: parametrosPadrao.percentualCustoOperacional,
          percentualMargemDeLucro: parametrosPadrao.percentualMargemDeLucro,
          percentualVariacaoOrcamento: parametrosPadrao.percentualVariacaoOrcamento,
          percentualComissaoVendas: parametrosPadrao.percentualComissaoVendas,
          percentualStudioArquiteto: parametrosPadrao.percentualStudioArquiteto,
          percentualMargemNegociacao: parametrosPadrao.percentualMargemNegociacao
        };
      },
      error: () => {
        this.toastService.show("Erro ao carregar parâmetros padrão", {
          classname: "bg-danger text-light",
        });
        this.orcamento = null;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  //CARREGA SOMENTE OS GRUPOS
  carregarListaDeItens(orcamentoId: string): void {

    this.budgetService.consultarGruposItens(orcamentoId);

    this.budgetService.grupos$.subscribe({
      next: (grupos: GruposItensDto | null) => {
        this.orcamentosRecebidos = grupos?.gruposItens;
      },
      error: () => {
        this.toastService.show("Erro ao carregar parâmetros", {
          classname: "bg-danger text-light",
        });
      }
    });
  }

  /**
   * Carrega as categorias disponíveis e converte em grupos vazios
   * Usado para modo criação (quando não há orcamentoId)
   */
  carregarCategoriasDisponiveis(): void {
    this.loading = true;

    this.budgetService.getGroups().subscribe({
      next: (categorias: BudgetCategory[]) => {
        // Converte BudgetCategory[] em Grupo[] (sem itensOrcamentos)
        this.orcamentosRecebidos = categorias
          .filter(cat => cat.apresentarEmOrcamento !== false) // Apenas categorias que devem aparecer no orçamento
          .sort((a, b) => (a.ordem || 0) - (b.ordem || 0)) // Ordena por ordem
          .map(categoria => {
            const grupo: Grupo = {
              id: categoria.id,
              nome: categoria.nome,
              apresentarEmOrcamento: categoria.apresentarEmOrcamento,
              tipoDeCalculo: categoria.tipoDeCalculo,
              tipoDeApresentacao: categoria.tipoDeApresentacao,
              valorTotal: 0,
              itensOrcamentos: [] // Grupos vazios para modo criação
            };
            return grupo;
          });
      },
      error: () => {
        this.toastService.show("Erro ao carregar categorias", {
          classname: "bg-danger text-light",
        });
        this.orcamentosRecebidos = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Método chamado quando o formulário de parâmetros emite mudanças
   * Atualiza o objeto orcamento para manter os valores sincronizados entre as abas
   */
  onOrcamentoChanged(orcamentoAtualizado: Orcamento): void {
    this.orcamento = orcamentoAtualizado;
  }

  /**
   * Verifica se está em modo edição (tem ID na rota)
   */
  isModoEdicao(): boolean {
    const orcamentoId = this.route.snapshot.paramMap.get('id');
    return !!(orcamentoId && orcamentoId !== 'novo');
  }

  /**
   * Verifica se o orçamento foi salvo (tem ID)
   * Usado para habilitar/desabilitar a navegação para a segunda aba
   * Só permite navegar se estiver no modo edição (já tem ID na rota)
   */
  temOrcamentoSalvo(): boolean {
    return this.isModoEdicao();
  }

  /**
   * Navega para a segunda aba (Orçamento) se o orçamento foi salvo
   */
  navegarParaOrcamento(): void {
    if (!this.isModoEdicao()) {
      this.toastService.show("É necessário salvar os parâmetros do orçamento antes de continuar", {
        classname: "bg-warning text-light",
      });
      return;
    }
    if (this.cdkStepper) {
      this.cdkStepper.next();
    }
  }

  /**
   * Avança para a próxima aba
   */
  avancar(): void {
    if (this.cdkStepper) {
      this.cdkStepper.next();
    }
  }

  /**
   * Volta para a aba anterior
   */
  voltar(): void {
    if (this.cdkStepper) {
      this.cdkStepper.previous();
    }
  }

  /**
   * Salva os parâmetros do orçamento (chama o método do componente de parâmetros)
   */
  salvarParametrosOrcamento(): void {
    if (this.formParametroComponent) {
      this.formParametroComponent.onSubmit();
    }
  }

  /**
   * Intercepta mudanças de seleção do stepper
   * Em modo criação: bloqueia navegação para abas 2 e 3
   * Em modo edição: permite navegação livre (incluindo voltar)
   */
  onStepSelectionChange(event: any): void {
    const newIndex = event.selectedIndex;
    
    // Se está em modo edição, permite TODA navegação (frente e trás) - não bloqueia nada
    if (this.isModoEdicao()) {
      return; // Permite navegar livremente, sem nenhum bloqueio
    }
    
    // Se está em modo criação (novo), bloqueia navegação para abas 2 e 3
    // Permite apenas ficar na aba 1 (índice 0)
    if (newIndex > 0) {
      if (this.cdkStepper) {
        setTimeout(() => {
          this.cdkStepper.selectedIndex = 0;
        }, 0);
        this.toastService.show("É necessário salvar os parâmetros do orçamento antes de continuar", {
          classname: "bg-warning text-light",
        });
      }
      return;
    }
  }

  verificarPermissoes(): void {
    this.podeEditar = this.authService.verificarPermissao('Doc_Orcamento', 'Editar');
  }
}
